/**
 * Browser dataLayer `sign_up` for listing/search alert enroll, plus
 * `save_search` for a search alert (GA4 gap 2026-10-22: a listing-alert signup
 * is a saved search, and GA4 only saw sign_up for most of them).
 *
 * Server Measurement Protocol never sends `sign_up`. Alert enroll still sends
 * server `generate_lead` (`lead_type: buyer_alerts`) — a different event.
 * `generate_lead` itself is server-only (`fireLeadGenerated`); this module
 * never pushes it.
 *
 * Pushes go through `trackEvent` → `pushDataLayerEvent` (GTM GA4 Event tag).
 * No `value` / `currency` (Matt 2026-10-08).
 */

import type { LeadFormId } from '@/lib/analytics/lead-event'
import { trackEvent } from '@/lib/tracking'

export type AlertSignupMethod = 'listing_alert' | 'search_alert'

export type AlertSignupFormId = Extract<LeadFormId, 'search_alert' | 'listing_save' | 'listing_price_watch'>

export type AlertSignupPayload = {
  event: 'sign_up'
  method: AlertSignupMethod
  form_id: AlertSignupFormId
  lead_type: 'buyer_alerts'
}

export function alertSignupPayload(input: {
  method: AlertSignupMethod
  form_id: AlertSignupFormId
}): AlertSignupPayload {
  return {
    event: 'sign_up',
    method: input.method,
    form_id: input.form_id,
    lead_type: 'buyer_alerts',
  }
}

/**
 * Search-alert forms. Each is a submitSearchAlertSignup call, which the server
 * counts as `generate_lead` with lead_type buyer_alerts. A saved home
 * (listing_save) or a price watch is one listing, not a search.
 */
export type SaveSearchFormId = Extract<LeadFormId, 'search_alert' | 'buyer_alerts_lp'>

/** Extra save_search facts from the saved filter (GA4 keeps only allowlisted names). */
export type SaveSearchParams = Record<string, string | number | boolean | undefined>

/**
 * `save_search` for one counted search-alert signup. lead_type and form_id
 * name the same lead the server's generate_lead carries.
 */
export function trackSaveSearch(input: { form_id: SaveSearchFormId; params?: SaveSearchParams }): void {
  trackEvent('save_search', {
    ...input.params,
    form_id: input.form_id,
    lead_type: 'buyer_alerts',
  })
}

/**
 * Listing/search alert enroll. GTM already allows `sign_up` and `save_search`.
 * A search alert (form_id search_alert) also sends save_search, with
 * `saveSearch` as its extra parameters.
 */
export function trackAlertSignup(input: {
  method: AlertSignupMethod
  form_id: AlertSignupFormId
  saveSearch?: SaveSearchParams
}): void {
  const payload = alertSignupPayload(input)
  trackEvent(payload.event, {
    method: payload.method,
    form_id: payload.form_id,
    lead_type: payload.lead_type,
  })
  if (payload.form_id === 'search_alert') trackSaveSearch({ form_id: 'search_alert', params: input.saveSearch })
}
